import { useTranslation } from 'react-i18next';
import type { CategoryOption } from '@/types';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { AuditList } from '@/components/admin/AuditList';
import { EventForm } from '@/components/admin/EventForm';
import { EMPTY_PAYLOAD, payloadFromEvent } from '@/components/admin/eventPayload';
import { SourceBadge, StatusBadge } from '@/components/admin/EventBadges';
import {
  AdminApiError,
  createAdminEvent,
  fetchAdminCategoryOptions,
  fetchAdminEvent,
  setAdminEventVisibility,
  unlockAdminEvent,
  updateAdminEvent,
} from '@/services/adminApi';
import { formatSqliteUtcDateTime } from '@/lib/date';
import type { AdminEvent, AuditEntry, EventPayload } from '@/types/admin';

function useCategories(reloadToken = 0) {
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesLoadFailed, setCategoriesLoadFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setCategoriesLoading(true);
    setCategoriesLoadFailed(false);
    fetchAdminCategoryOptions(controller.signal)
      .then((options) => {
        if (!controller.signal.aborted) setCategories(options);
      })
      .catch(() => {
        if (!controller.signal.aborted) setCategoriesLoadFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setCategoriesLoading(false);
      });
    return () => controller.abort();
  }, [reloadToken]);
  return { categories, categoriesLoading, categoriesLoadFailed };
}

function BackLink() {
  const { t } = useTranslation('admin');
  return (
    <Link to="/admin" className="text-link">
      ← {t('events.back')}
    </Link>
  );
}

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className="font-mono text-xs">{children}</dd>
    </div>
  );
}

function EditEvent({ eventId }: { eventId: number }) {
  const { t } = useTranslation('admin');
  const location = useLocation();
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const { categories, categoriesLoading, categoriesLoadFailed } = useCategories(reloadToken);

  useEffect(() => {
    if (location.state && (location.state as { created?: boolean }).created) {
      setNotice('events.created');
    }
  }, [location.state]);

  useEffect(() => {
    const controller = new AbortController();
    fetchAdminEvent(eventId, controller.signal)
      .then((result) => {
        setEvent(result.event);
        setAudit(result.audit);
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        let message = 'events.loadFailed';
        if (reason instanceof AdminApiError && reason.status === 404) {
          message = 'events.notFound';
        }
        setLoadError(message);
      });
    return () => controller.abort();
  }, [eventId, reloadToken]);

  const runAction = async (action: () => Promise<AdminEvent>, message: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      setNotice(message);
      setReloadToken((token) => token + 1);
    } catch (reason) {
      let text = 'events.actionFailed';
      if (reason instanceof AdminApiError) {
        text = reason.message;
      }
      setActionError(text);
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (payload: EventPayload) => {
    await updateAdminEvent(eventId, payload);
    setNotice('events.saved');
    setReloadToken((token) => token + 1);
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <BackLink />
        <p className="m-0 text-sm text-ink-2">{t(loadError, { defaultValue: loadError })}</p>
      </div>
    );
  }
  if (!event) {
    return (
      <p className="kicker m-0 py-12" aria-live="polite">{t('loading')}</p>
    );
  }

  const visible = event.is_enabled === 1;
  let visibilityLabel = 'events.unhide';
  let visibilityMessage = 'events.shown';
  let visibilityVariant: 'default' | 'destructive' = 'default';
  if (visible) {
    visibilityLabel = 'events.hide';
    visibilityMessage = 'events.hidden';
    visibilityVariant = 'destructive';
  }
  const toggleVisibility = () => {
    if (visible && !window.confirm(t('events.hideConfirm'))) {
      return;
    }
    runAction(() => setAdminEventVisibility(eventId, !visible), visibilityMessage);
  };

  return (
    <div>
      <BackLink />
      <header className="mt-4 flex flex-col gap-2 border-b border-rule pb-5">
        <span className="kicker">{t('events.edit')}</span>
        <h1 className="m-0 text-[1.75rem] md:text-[2rem]">{event.title}</h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <StatusBadge enabled={visible} />
          <SourceBadge event={event} />
        </div>
      </header>
      {notice && (
        <p role="status" className="callout callout-success m-0 mt-4">
          {t(notice)}
        </p>
      )}
      {actionError && (
        <p role="alert" className="callout callout-danger m-0 mt-4">
          {t(actionError, { defaultValue: actionError })}
        </p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-12">
        <div className="md:col-span-8">
          {event.source === 'crawler' && event.admin_locked === 0 && (
            <p className="callout callout-info m-0 mb-5 text-xs">
              {t('events.crawlerEditHint')}
            </p>
          )}
          <EventForm
            key={event.updated_at}
            initial={payloadFromEvent(event)}
            typeEditable={false}
            categories={categories}
            categoriesLoading={categoriesLoading}
            categoriesLoadFailed={categoriesLoadFailed}
            submitLabel={t('events.save')}
            onSubmit={handleSave}
          />
        </div>

        <aside className="flex flex-col gap-8 md:col-span-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('actions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant={visibilityVariant}
                className="w-full"
                disabled={busy}
                onClick={toggleVisibility}
              >
                {t(visibilityLabel)}
              </Button>
              {event.source === 'crawler' && event.admin_locked === 1 && (
                <div className="space-y-1.5">
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={busy}
                    onClick={() => runAction(() => unlockAdminEvent(eventId), 'events.unlocked')}
                  >
                    {t('events.unlock')}
                  </Button>
                  <p className="m-0 text-xs leading-relaxed text-ink-2">
                    {t('events.unlockHint')}
                  </p>
                </div>
              )}
              <p className="m-0 text-xs leading-relaxed text-ink-2">
                {t('events.hideHint')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('events.info')}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <dl className="data-list m-0">
                <MetaRow label={t('fields.uid')}>{event.uid}</MetaRow>
                {event.seed_id && <MetaRow label={t('fields.seed_id')}>{event.seed_id}</MetaRow>}
                {event.source_id && <MetaRow label={t('fields.source_id')}>{event.source_id}</MetaRow>}
                {event.source_event_id && <MetaRow label={t('fields.source_event_id')}>{event.source_event_id}</MetaRow>}
                {event.last_seen_at && (
                  <MetaRow label={t('fields.last_seen_at')}>{formatSqliteUtcDateTime(event.last_seen_at)}</MetaRow>
                )}
                <MetaRow label={t('fields.created_at')}>{formatSqliteUtcDateTime(event.created_at)}</MetaRow>
                <MetaRow label={t('fields.updated_at')}>{formatSqliteUtcDateTime(event.updated_at)}</MetaRow>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('nav.audit')}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <AuditList entries={audit} />
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function NewEvent() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { categories, categoriesLoading, categoriesLoadFailed } = useCategories();

  const handleCreate = async (payload: EventPayload) => {
    const created = await createAdminEvent(payload);
    navigate(`/admin/events/${created.id}`, { state: { created: true } });
  };

  return (
    <div>
      <BackLink />
      <header className="mt-4 border-b border-rule pb-5">
        <h1 className="m-0 text-[1.75rem] md:text-[2rem]">{t('events.new')}</h1>
      </header>
      <div className="mt-6 max-w-2xl">
        <EventForm
          initial={EMPTY_PAYLOAD}
          typeEditable
          categories={categories}
          categoriesLoading={categoriesLoading}
          categoriesLoadFailed={categoriesLoadFailed}
          submitLabel={t('events.create')}
          onSubmit={handleCreate}
        />
      </div>
    </div>
  );
}

export function AdminEventEditPage() {
  const { id } = useParams();
  const eventId = Number(id);
  let content = <NewEvent />;
  if (id !== undefined) {
    content = <EditEvent key={eventId} eventId={eventId} />;
  }
  return <AdminLayout>{content}</AdminLayout>;
}
